import { v3 } from '../math.ts';
import { box, type LevelGeometry } from '../level.ts';
import { makePool, makeShrine, type Arena } from '../arena.ts';
import { VALHALLA_WALK } from '../constants.ts';

/**
 * Two gray-box arenas, so the rotation has somewhere to rotate to.
 *
 * A map pool of one is not a pool, and a best-of-five that plays the Cathedral
 * five times is not the feature anybody asked for — so the round-transition work
 * needed at least two more maps to be worth anything, and these are them.
 *
 * They are deliberately *shapes* rather than places. The Castle Courtyard is
 * open with a keep in the middle: long sightlines, few corners, and a raised
 * centre worth holding — the arena where a Magician's arcs pay and a Mentalist's
 * flight is the fastest way anywhere. The Machiro Bazaar is the opposite: a
 * dense grid of stalls, no sightline longer than about fifteen metres, and every
 * junction a place to lay a Sign. Between them and the Cathedral's rooms-and-
 * corridors, a pool of three asks a player three different questions.
 *
 * Both are built from the same primitives as the Cathedral's gray-box, and both
 * are meant to be replaced by authored geometry. What they are for today is
 * making the rotation real: three maps that are visibly different when the
 * scoreboard says the next round is somewhere else.
 */

const H = 9;       // interior height
const T = 1;       // wall thickness

/** Floor, ceiling and four walls for a square arena of the given width. */
function shell(w: number): ReturnType<typeof box>[] {
  const half = w / 2;
  return [
    box(0, -0.5, 0, w, 1, w, 'floor', 'shell'),
    box(0, H + 0.5, 0, w, 1, w, 'ceiling', 'shell'),
    box(0, H / 2, -half, w, H, T, 'wall', 'shell'),
    box(0, H / 2, half, w, H, T, 'wall', 'shell'),
    box(-half, H / 2, 0, T, H, w, 'wall', 'shell'),
    box(half, H / 2, 0, T, H, w, 'wall', 'shell'),
  ];
}

/**
 * Valhalla, well below the floor and sealed.
 *
 * Identical in both because it is not a place anybody plays — it is a corridor
 * with a teleporter at the end of it, and giving each arena its own would be
 * three ways to write the same room.
 */
const VY = -40;
const VHALFW = 5;

const valhalla = (): Arena['valhalla'] => [{
  pos: v3(0, VY, -VALHALLA_WALK / 2),
  radius: VHALFW,
  exits: [v3(0, VY, VALHALLA_WALK / 2)],
}];

const underworld = (): ReturnType<typeof box>[] => [
  box(0, VY - 0.5, 0, VHALFW * 2 + T * 2, 1, VALHALLA_WALK + T * 2, 'floor', 'valhalla'),
  box(0, VY + 6.5, 0, VHALFW * 2 + T * 2, 1, VALHALLA_WALK + T * 2, 'ceiling', 'valhalla'),
  box(-VHALFW, VY + 3, 0, T, 6, VALHALLA_WALK, 'wall', 'valhalla'),
  box(VHALFW, VY + 3, 0, T, 6, VALHALLA_WALK, 'wall', 'valhalla'),
  box(0, VY + 3, -VALHALLA_WALK / 2, VHALFW * 2, 6, T, 'wall', 'valhalla'),
  box(0, VY + 3, VALHALLA_WALK / 2, VHALFW * 2, 6, T, 'wall', 'valhalla'),
];

// ── Castle Courtyard ─────────────────────────────────────────────────────────

const CY_W = 76;

/**
 * Open ground around a central keep, with a wall-walk over it.
 *
 * The keep is the whole map: it blocks the one sightline that would otherwise
 * run corner to corner, it is the only high ground, and the ramps onto it are
 * where the fighting ends up. Three towers at the corners give each side
 * something to fall back to that is not their own shrine.
 */
export const CASTLE_COURTYARD_GEOMETRY: LevelGeometry = {
  name: 'Castle Courtyard',
  // Brighter and longer-reaching than the Cathedral's: this one is meant to read
  // as outdoors, and its sightlines are only worth having if they can be seen.
  ambient: 0.5,
  torchIntensity: 55,
  torchRange: 38,
  boxes: [
    ...shell(CY_W),
    ...underworld(),

    /*
     * The keep: a solid block with a battlemented roof and a stair up each side.
     *
     * The first version reached the roof by two floating slabs at different
     * heights, which did the job and looked like scaffolding — horizontal
     * rectangles jutting out of a tower at nothing in particular, with a gap you
     * stepped over rather than climbed. A castle does not have those. It has a
     * stair against the wall, with something holding it up.
     *
     * So: a run of steps rising along each flank, carried on a spur wall that
     * meets the ground, with a landing at the top. The steps are what you walk;
     * the spur is what makes them read as masonry rather than as floating stone.
     */
    box(0, 3, 0, 18, 6, 18, 'wall', 'keep'),
    box(0, 6.4, 0, 20, 0.8, 20, 'platform', 'keep'),

    // Battlements: merlons round the roof edge, so the high ground has cover on
    // it rather than being a bare table somebody can be shot off from anywhere.
    ...([-8, -4, 0, 4, 8] as const).flatMap((n) => [
      box(n, 7.5, -9.6, 2.4, 1.4, 1.2, 'wall', 'merlon'),
      box(n, 7.5, 9.6, 2.4, 1.4, 1.2, 'wall', 'merlon'),
      box(-9.6, 7.5, n, 1.2, 1.4, 2.4, 'wall', 'merlon'),
      box(9.6, 7.5, n, 1.2, 1.4, 2.4, 'wall', 'merlon'),
    ]),

    /*
     * The stairs themselves: eight steps a side, climbing from the courtyard to
     * the roof. Built from a rule rather than listed, so the rise stays even and
     * the treads stay walkable when somebody changes the keep's height.
     */
    ...([-1, 1] as const).flatMap((side) =>
      Array.from({ length: 8 }, (_, n) => {
        const rise = 6.8 / 8;
        const tread = 1.5;
        const x = side * (10.5 + (7 - n) * tread);
        return box(x, (n + 1) * rise * 0.5, 0, tread, (n + 1) * rise, 4.4, 'platform', 'keep-stair');
      }),
    ),
    // The spur each stair runs against, so it is carried by masonry rather than
    // ending in mid-air at the side.
    ...([-1, 1] as const).flatMap((side) => [
      box(side * 16, 2.2, -2.8, 13, 4.4, 0.9, 'wall', 'stair-spur'),
      box(side * 16, 2.2, 2.8, 13, 4.4, 0.9, 'wall', 'stair-spur'),
    ]),

    /*
     * Towers on the north corners and the flanks — not the south ones.
     *
     * The first pass put one in each corner and two of them landed on top of the
     * Balance and Chaos shrines, which the arena tests caught by walking every
     * spawn and finding it inside a wall. Worth having happened: a spawn inside
     * geometry is not something a screenshot shows.
     */
    ...([[-26, -26], [26, -26], [-30, 2], [30, 2]] as const).flatMap(([x, z]) => [
      box(x, H / 2, z, 10, H, 10, 'wall', 'tower'),
      box(x, 4.6, z, 12, 0.6, 12, 'platform', 'tower'),
    ]),

    /*
     * Low barricades breaking the open ground between keep and towers.
     *
     * Clear of z=0 on the east and west, because that is where the keep stairs
     * now run — the first version had a three-metre barricade standing in the
     * middle of the staircase, which a floor-height probe caught as a step that
     * climbed from 0.85 to 3.0 and back down to 2.55.
     */
    ...([-1, 1] as const).flatMap((s) => [
      box(s * 20, 1.5, 12, 1.4, 3, 9, 'wall', 'barricade'),
      box(s * 20, 1.5, -12, 1.4, 3, 9, 'wall', 'barricade'),
      box(0, 1.5, s * 20, 14, 3, 1.4, 'wall', 'barricade'),
    ]),

    /*
     * A ruined curtain wall across the south, between the Balance and Chaos
     * shrines.
     *
     * Those two sit on the same edge of the map with thirty metres of flat
     * nothing between them, which meant each could be fought from the other's
     * doorstep with no way to break contact — the worst kind of open ground,
     * because it is not a sightline anybody chose to take, it is one they cannot
     * avoid. A straight wall would fix the sightline and wall off a third of the
     * arena, so this is a broken one: three stretches with gaps you can run
     * through, and a gatehouse in the middle tall enough to stop the long shot.
     *
     * Gaps rather than a barrier because the point is cover, not separation. You
     * should be able to cross, and have to choose where.
     */
    /*
     * Sat at z≈30, which is the line the two shrines actually see each other
     * along — a wall short of it looks like cover and stops nothing. The
     * segments are kept inboard of x=±15 because that is where the Balance and
     * Chaos spawns are, and a wall through a spawn is how the arena tests found
     * the first attempt at this.
     */
    box(0, 3.5, 29.5, 11, 7, 4, 'wall', 'gatehouse'),
    box(0, 7.6, 29.5, 13, 1.2, 5, 'platform', 'gatehouse'),
    ...([-1, 1] as const).flatMap((s) => [
      box(s * 10.5, 2, 29.5, 9, 4, 2.2, 'wall', 'curtain'),
      // A stub of fallen wall out in the gap: something to take a knee behind on
      // the run between sections, so crossing is a choice rather than a dash
      // across open ground.
      box(s * 12, 1.3, 25, 5, 2.6, 1.8, 'wall', 'rubble'),
    ]),

    // Shrine daises.
    box(0, 0.3, -30, 10, 0.6, 10, 'platform', 'order-shrine'),
    box(-26, 0.3, 30, 10, 0.6, 10, 'platform', 'balance-shrine'),
    box(26, 0.3, 30, 10, 0.6, 10, 'platform', 'chaos-shrine'),
  ],
  torches: [
    v3(0, -36, -20), v3(0, -36, 0), v3(0, -36, 20),
    v3(0, 7.4, 0),
    v3(-26, 5.4, -26), v3(26, 5.4, -26), v3(-26, 5.4, 26), v3(26, 5.4, 26),
    v3(0, 3, -30), v3(0, 3, 30), v3(-30, 3, 0), v3(30, 3, 0),
  ].map((pos) => ({ pos, yaw: 0 })),
};

export function makeCastleCourtyard(): Arena {
  return {
    name: 'Castle Courtyard',
    shrines: [
      makeShrine('order', v3(0, 0.6, -30), v3(0, 0.6, -24)),
      makeShrine('balance', v3(-26, 0.6, 30), v3(-22, 0.6, 25)),
      makeShrine('chaos', v3(26, 0.6, 30), v3(22, 0.6, 25)),
    ],
    /*
     * Pools on the ground, including the keep's.
     *
     * The first pass put one on the keep roof, on the theory that a pool nobody
     * can shoot at from below is worth climbing for. The arena tests disagreed
     * and were right: the nav grid is a floor plan, so a pool reachable only by
     * ramp is a pool no bot ever contests and one the reachability check calls
     * cut off. The roof is still the high ground; it just is not an objective.
     */
    pools: [
      makePool('cy_keep', 'Keep Gate', v3(0, 0, -13)),
      makePool('cy_north', 'North Ward', v3(0, 0, -22)),
      makePool('cy_south', 'South Ward', v3(0, 0, 22)),
      makePool('cy_west', 'West Ward', v3(-22, 0, 0)),
      makePool('cy_east', 'East Ward', v3(22, 0, 0)),
      makePool('cy_nw', 'Northwest Tower', v3(-20, 0, -20)),
      makePool('cy_ne', 'Northeast Tower', v3(20, 0, -20)),
    ],
    valhalla: valhalla(),
    spawns: {
      order: [v3(0, 0.6, -24), v3(4, 0.6, -27), v3(-4, 0.6, -27)],
      balance: [v3(-22, 0.6, 25), v3(-27, 0.6, 27), v3(-18, 0.6, 28)],
      chaos: [v3(22, 0.6, 25), v3(27, 0.6, 27), v3(18, 0.6, 28)],
    },
  };
}

// ── Machiro Bazaar ───────────────────────────────────────────────────────────

const BZ_W = 70;

/**
 * A grid of market stalls: no long sightlines, and a corner every few metres.
 *
 * The opposite question to the Courtyard. Nothing here rewards an arc across the
 * map because there is nowhere to draw one — it is a map about knowing where the
 * next junction leads, which is where Signs, walls and short-range explosions
 * earn their place.
 *
 * The stalls are laid out on a grid with two lanes cut through it, so the map
 * has a spine without having a sightline.
 */
export const MACHIRO_BAZAAR_GEOMETRY: LevelGeometry = {
  name: 'Machiro Bazaar',
  // Dimmer and shorter-reaching, so the stalls actually hide things. A map about
  // corners is not much of one if every corner is lit from across the market.
  ambient: 0.34,
  torchIntensity: 48,
  torchRange: 22,
  boxes: [
    ...shell(BZ_W),
    ...underworld(),

    /*
     * Stalls on a seven-metre grid, with the two central lanes left clear.
     *
     * Built rather than listed: thirty-odd blocks written out by hand is thirty
     * chances to typo a coordinate, and the layout is the *rule* rather than the
     * numbers — which is also what makes it easy to try a different one.
     */
    ...([-24, -16, -8, 8, 16, 24] as const).flatMap((x) =>
      ([-24, -16, -8, 8, 16, 24] as const)
        // The crossroads at the centre stays open...
        .filter((z) => !(Math.abs(x) === 8 && Math.abs(z) === 8))
        // ...and so do the two southern corners, which are shrine ground. The
        // arena tests found Balance and Chaos spawning inside a stall here.
        .filter((z) => !(Math.abs(x) === 24 && z === 24))
        .map((z) => box(x, 2, z, 5, 4, 5, 'wall', 'stall')),
    ),

    // Awnings over some stalls — waist-high cover a Mentalist can get above.
    ...([-16, 16] as const).flatMap((x) =>
      ([-16, 16] as const).map((z) => box(x, 4.4, z, 7, 0.5, 7, 'platform', 'awning')),
    ),

    // The two lanes, walled at their outer ends so they are routes not tunnels.
    box(0, H / 2, -30, 8, H, T, 'wall', 'lane'),
    box(0, H / 2, 30, 8, H, T, 'wall', 'lane'),

    // A raised gallery along one wall, reached from the awnings.
    box(-31, 4.6, 0, 6, 0.6, 40, 'platform', 'gallery'),

    box(0, 0.3, -28, 9, 0.6, 9, 'platform', 'order-shrine'),
    box(-27, 0.3, 27, 9, 0.6, 9, 'platform', 'balance-shrine'),
    box(27, 0.3, 27, 9, 0.6, 9, 'platform', 'chaos-shrine'),
  ],
  torches: [
    v3(0, -36, -20), v3(0, -36, 0), v3(0, -36, 20),
    v3(0, 3, 0),
    ...([-20, 0, 20] as const).flatMap((x) =>
      ([-20, 0, 20] as const).map((z) => v3(x, 3, z)),
    ),
    v3(-30, 5.4, -12), v3(-30, 5.4, 12),
  ].map((pos) => ({ pos, yaw: 0 })),
};

export function makeMachiroBazaar(): Arena {
  return {
    name: 'Machiro Bazaar',
    shrines: [
      makeShrine('order', v3(0, 0.6, -28), v3(0, 0.6, -22)),
      makeShrine('balance', v3(-27, 0.6, 27), v3(-23, 0.6, 22)),
      makeShrine('chaos', v3(27, 0.6, 27), v3(23, 0.6, 22)),
    ],
    pools: [
      makePool('bz_cross', 'The Crossroads', v3(0, 0, 0)),
      makePool('bz_north', 'Spice Row', v3(0, 0, -20)),
      makePool('bz_south', 'Cloth Row', v3(0, 0, 20)),
      makePool('bz_west', 'The Gallery', v3(-20, 0, 0)),
      makePool('bz_east', 'Coppersmiths', v3(20, 0, 0)),
      makePool('bz_nw', 'North Gate', v3(-20, 0, -20)),
      makePool('bz_se', 'South Gate', v3(20, 0, 20)),
    ],
    valhalla: valhalla(),
    spawns: {
      order: [v3(0, 0.6, -22), v3(3, 0.6, -25), v3(-3, 0.6, -25)],
      balance: [v3(-23, 0.6, 22), v3(-26, 0.6, 24), v3(-20, 0.6, 25)],
      chaos: [v3(23, 0.6, 22), v3(26, 0.6, 24), v3(20, 0.6, 25)],
    },
  };
}
